import React from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@mui/material";
import {
  TrendingUp,
  People,
  Speed,
  Assessment,
  Campaign,
  Analytics,
  CheckCircle,
  EmojiEvents,
} from "@mui/icons-material";

export default function MarketingStatistics() {
  const statistics = [
    {
      title: "Client Success Rate",
      value: "95%",
      description: "Our clients achieve their marketing goals",
      icon: <EmojiEvents sx={{ fontSize: 40, color: "primary.main" }} />,
    },
    {
      title: "Average ROI",
      value: "300%",
      description: "Return on investment for our clients",
      icon: <TrendingUp sx={{ fontSize: 40, color: "primary.main" }} />,
    },
    {
      title: "Active Campaigns",
      value: "500+",
      description: "Currently running marketing campaigns",
      icon: <Campaign sx={{ fontSize: 40, color: "primary.main" }} />,
    },
    {
      title: "Monthly Reports",
      value: "1000+",
      description: "Detailed performance reports generated",
      icon: <Assessment sx={{ fontSize: 40, color: "primary.main" }} />,
    },
  ];

  const keyMetrics = [
    {
      title: "Search Engine Optimization",
      metrics: [
        "Average 150% increase in organic traffic",
        "Top 3 rankings for 80% of target keywords",
        "95% client retention rate",
        "Average 200% increase in conversion rate",
      ],
    },
    {
      title: "Social Media Marketing",
      metrics: [
        "Average 200% increase in engagement rate",
        "50% growth in follower base",
        "85% positive sentiment in comments",
        "40% increase in social media leads",
      ],
    },
    {
      title: "Content Marketing",
      metrics: [
        "Average 180% increase in content engagement",
        "70% higher time on site",
        "45% increase in backlinks",
        "60% growth in email subscribers",
      ],
    },
    {
      title: "Paid Advertising",
      metrics: [
        "Average 250% ROI on ad spend",
        "40% lower cost per acquisition",
        "65% higher conversion rate",
        "30% increase in customer lifetime value",
      ],
    },
  ];

  return (
    
    <Box component="section" sx={{ bgcolor: "#f5f5f5", minHeight: "100vh", py: 8 }}>
      {/* Hero Section */}
      <Container maxWidth="lg" sx={{ mb: 8 }}>
        <Typography
          component="h1"
          variant="h2"
          sx={{
            textAlign: "center",
            mb: 3,
            fontFamily: "Nunito, serif",
            fontSize: { xs: "2rem", md: "3rem" },
            fontWeight: "bold",
          }}
        >
          Marketing Statistics & Performance Metrics
        </Typography>
        <Typography
          variant="h6"
          sx={{
            textAlign: "center",
            color: "text.secondary",
            maxWidth: 800,
            mx: "auto",
            mb: 6,
            fontFamily: "Nunito, serif",
          }}
        >
          Discover how our data-driven approach and proven strategies have helped businesses achieve remarkable growth and success.
        </Typography>
      </Container>

      {/* Statistics Cards */}
      <Container maxWidth="lg" sx={{ mb: 8 }}>
        <Grid container spacing={4}>
          {statistics.map((stat, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                  p: 3,
                  transition: "transform 0.3s",
                  "&:hover": {
                    transform: "translateY(-5px)",
                  },
                }}
              >
                {stat.icon}
                <Typography
                  variant="h3"
                  sx={{
                    my: 2,
                    fontWeight: "bold",
                    color: "primary.main",
                  }}
                >
                  {stat.value}
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    mb: 1,
                    fontFamily: "Nunito, serif",
                  }}
                >
                  {stat.title}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                  }}
                >
                  {stat.description}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Key Performance Metrics */}
      <Container maxWidth="lg">
        <Typography
          component="h2"
          variant="h3"
          sx={{
            textAlign: "center",
            mb: 6,
            fontFamily: "Nunito, serif",
            fontSize: { xs: "1.75rem", md: "2.5rem" },
          }}
        >
          Key Performance Metrics
        </Typography>
        <Grid container spacing={4}>
          {keyMetrics.map((metric, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Card
                sx={{
                  height: "100%",
                  p: 3,
                  transition: "transform 0.3s",
                  "&:hover": {
                    transform: "translateY(-5px)",
                  },
                }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    mb: 3,
                    fontFamily: "Nunito, serif",
                    color: "primary.main",
                  }}
                >
                  {metric.title}
                </Typography>
                <List disablePadding>
                  {metric.metrics.map((item, idx) => (
                    <React.Fragment key={idx}>
                      <ListItem disableGutters>
                        <ListItemIcon>
                          <CheckCircle sx={{ color: "primary.main", fontSize: 20 }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={item}
                          sx={{
                            "& .MuiListItemText-primary": {
                              fontFamily: "Nunito, serif",
                            },
                          }}
                        />
                      </ListItem>
                      {idx < metric.metrics.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}
